import { useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useGetIdentity } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import type { CommissionsScope, PeriodKey } from '../types';
import { useWidgetData } from '../useWidgetData';
import { formatCurrency } from '../utils';
import { COLOR_CONVERTED, LABEL_COLOR } from '../constants';
import { WidgetCard } from './WidgetCard';
import { WidgetScopeToggle } from './WidgetScopeToggle';

interface ActiveProjectsWidgetProps {
    period: PeriodKey;
    enabled: boolean;
    contractorId?: number;
}

export const ActiveProjectsWidget = ({
    period,
    enabled,
    contractorId,
}: ActiveProjectsWidgetProps) => {
    const navigate = useNavigate();
    const { identity } = useGetIdentity();
    const isSuperuser = Boolean((identity as { administrator?: boolean } | undefined)?.administrator);
    const [scope, setScope] = useState<CommissionsScope>('all');
    const { data, isLoading, error } = useWidgetData('activeProjects', period, enabled, {
        contractorId,
        scope,
    });

    // Superusers switch between all users' totals and their own projects; others always see their own.
    const scopeToggle = isSuperuser ? <WidgetScopeToggle value={scope} onChange={setScope} /> : undefined;

    if (error) {
        return (
            <WidgetCard
                title="Active Projects"
                subtitle="By contractor and project cost"
                action={scopeToggle}
                children={
                    <Typography variant="body2" sx={{ color: '#EF4444' }}>
                        Failed to load projects widget
                    </Typography>
                }
            />
        );
    }

    if (isLoading || !data) {
        return (
            <WidgetCard
                title="Active Projects"
                subtitle="By contractor and project cost"
                action={scopeToggle}
                loading
            />
        );
    }

    const isTeam = data.scope === 'team';
    // All users shows totals, so it is empty only when no projects match; Mine lists rows.
    const isEmpty = isTeam ? data.project_count === 0 : !(data.rows && data.rows.length > 0);
    const headline = (
        <Stack direction="row" alignItems="baseline" spacing={1}>
            <Typography
                sx={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: '#111827',
                }}
            >
                {formatCurrency(data.total_cost)}
            </Typography>
            <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                {data.project_count} project{data.project_count !== 1 ? 's' : ''}
            </Typography>
        </Stack>
    );

    return (
        <WidgetCard
            title="Active Projects"
            subtitle={`${isTeam ? 'All users' : 'Mine'} · approved this ${period}`}
            action={scopeToggle}
            headline={headline}
        >
            {isEmpty ? (
                <Typography variant="body2" sx={{ color: LABEL_COLOR }}>
                    No data for this period
                </Typography>
            ) : isTeam ? (
                <Stack spacing={1.5}>
                    {(data.by_user ?? []).map((row) => (
                        <Box
                            key={row.user_id ?? row.user_name}
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: '#F9FAFB',
                                p: 1.5,
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={1}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, color: '#111827', mb: 0.25 }}
                                    >
                                        {row.user_name}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                                        {row.project_count} project{row.project_count !== 1 ? 's' : ''}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}
                                    >
                                        {formatCurrency(row.total_cost)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 600, color: COLOR_CONVERTED, whiteSpace: 'nowrap', display: 'block' }}
                                    >
                                        {formatCurrency(row.commission_total)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            ) : (
                <Stack spacing={1.5}>
                    {data.rows.map((row) => (
                        <Box
                            key={row.project_id}
                            sx={{
                                borderRadius: '8px',
                                backgroundColor: '#F9FAFB',
                                p: 1.5,
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                    backgroundColor: '#F3F4F6',
                                },
                            }}
                            onClick={() => navigate(`/projects/${row.project_id}/show`)}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="flex-start"
                                spacing={1}
                            >
                                <Box sx={{ flex: 1 }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500, color: '#111827', mb: 0.25 }}
                                    >
                                        {row.project_no ?? row.project_name} • {row.address}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: LABEL_COLOR }}>
                                        {row.contractor_name}
                                    </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 600, color: '#111827', whiteSpace: 'nowrap' }}
                                    >
                                        {formatCurrency(row.cost)}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ fontWeight: 600, color: COLOR_CONVERTED, whiteSpace: 'nowrap', display: 'block' }}
                                    >
                                        {formatCurrency(row.commission)}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            )}
        </WidgetCard>
    );
};

ActiveProjectsWidget.displayName = 'ActiveProjectsWidget';
